import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Trash2, Plus, Save, Package, Users, Settings as Cog, Upload, QrCode } from "lucide-react";

const CATEGORIES = [
  { id: "focacce", label: "Focacce" },
  { id: "dirty_sides", label: "Dirty Sides" },
  { id: "dolci", label: "Dolci" },
  { id: "drink", label: "Drink" },
];

const EMPTY = { name: "", slug: "", category: "focacce", ingredients: "", description: "", allergens: "",
  price: "", image: "", featured: false, badge: "", club_only: false, available: true, sort_order: 0 };

function ProductForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || EMPTY);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/images", fd);
      set("image", data.url);
    } catch (err) {
      alert("Upload fallito: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-[#0C0C0C] border border-[#333] rounded-xl p-4 mt-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Nome" value={f.name} onChange={(e) => set("name", e.target.value)} className="ung-input" data-testid="pf-name" />
        <input placeholder="Slug (auto)" value={f.slug} onChange={(e) => set("slug", e.target.value)} className="ung-input" data-testid="pf-slug" />
        <select value={f.category} onChange={(e) => set("category", e.target.value)} className="ung-input" data-testid="pf-category">
          {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <input placeholder="Prezzo (es. 6.90)" value={f.price ?? ""} onChange={(e) => set("price", e.target.value)} className="ung-input" data-testid="pf-price" />
      </div>

      <div className="mt-3">
        <span className="text-white/60 text-sm font-semibold uppercase">Foto prodotto</span>
        <div className="flex items-center gap-3 mt-1">
          <div className="w-16 h-16 rounded-lg bg-[#141414] border border-[#333] overflow-hidden shrink-0">
            {f.image ? <img src={f.image} alt="anteprima" className="w-full h-full object-cover" data-testid="pf-image-preview" /> : null}
          </div>
          <div className="flex-1">
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="pf-image-file" />
            <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2" data-testid="pf-upload-btn">
              <Upload size={16} /> {uploading ? "Caricamento…" : "Carica foto"}
            </button>
            <input placeholder="oppure incolla /assets/…jpg o URL" value={f.image} onChange={(e) => set("image", e.target.value)} className="ung-input mt-2" data-testid="pf-image" />
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <input placeholder="Ingredienti" value={f.ingredients} onChange={(e) => set("ingredients", e.target.value)} className="ung-input sm:col-span-2" data-testid="pf-ingredients" />
        <input placeholder="Descrizione (es. Dirty Sides)" value={f.description || ""} onChange={(e) => set("description", e.target.value)} className="ung-input sm:col-span-2" data-testid="pf-description" />
        <input placeholder="Allergeni (vuoto = non mostrati)" value={f.allergens || ""} onChange={(e) => set("allergens", e.target.value)} className="ung-input sm:col-span-2" data-testid="pf-allergens" />
        <input placeholder="Badge (opzionale)" value={f.badge} onChange={(e) => set("badge", e.target.value)} className="ung-input" data-testid="pf-badge" />
        <input type="number" placeholder="Ordine" value={f.sort_order} onChange={(e) => set("sort_order", Number(e.target.value))} className="ung-input" data-testid="pf-order" />
      </div>
      <div className="flex flex-wrap gap-4 mt-3 text-white/70 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.featured} onChange={(e) => set("featured", e.target.checked)} className="accent-[#FFC20E]" /> Featured</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.available} onChange={(e) => set("available", e.target.checked)} className="accent-[#FFC20E]" /> Disponibile</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={f.club_only} onChange={(e) => set("club_only", e.target.checked)} className="accent-[#FFC20E]" /> Solo Club</label>
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={() => onSave({ ...f, price: f.price === "" || f.price == null ? null : Number(f.price) })} className="btn-yellow" data-testid="pf-save"><Save size={16} /> Salva</button>
        <button onClick={onCancel} className="btn-ghost" data-testid="pf-cancel">Annulla</button>
      </div>
    </div>
  );
}

function SettingsGroup({ title, section, fields, settings, setSettings }) {
  const val = settings[section] || {};
  const upd = (k, v) => setSettings({ ...settings, [section]: { ...val, [k]: v } });
  return (
    <fieldset className="bg-[#141414] border border-[#242424] rounded-xl p-4">
      <legend className="font-display font-black uppercase text-[#FFC20E] px-2">{title}</legend>
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map(([k, label, type]) => (type === "bool" ? (
          <label key={k} className="flex items-center gap-2 text-white/70 text-sm">
            <input type="checkbox" checked={!!val[k]} onChange={(e) => upd(k, e.target.checked)} className="accent-[#FFC20E]" data-testid={`set-${section}-${k}`} /> {label}
          </label>
        ) : (
          <label key={k} className="text-white/50 text-xs">{label}
            <input className="ung-input mt-1" type={type === "num" ? "number" : "text"} step="0.01" value={val[k] ?? ""}
              onChange={(e) => upd(k, type === "num" ? Number(e.target.value) : e.target.value)} data-testid={`set-${section}-${k}`} />
          </label>
        )))}
      </div>
    </fieldset>
  );
}

const EXTRA_GROUPS = [
  { title: "Social, telefono & recensioni", section: "links", fields: [["instagram_enabled", "Instagram visibile", "bool"], ["instagram_url", "instagram_url"], ["tiktok_enabled", "TikTok visibile", "bool"], ["tiktok_url", "tiktok_url"], ["phone_enabled", "Telefono visibile", "bool"], ["phone_number", "phone_number"], ["google_review_enabled", "Google Reviews visibile", "bool"], ["google_review_url", "google_review_url"], ["whatsapp_enabled", "WhatsApp visibile", "bool"], ["whatsapp_number", "whatsapp_number"], ["whatsapp_message", "Messaggio WhatsApp precompilato"]] },
  { title: "Combo", section: "combo", fields: [["enabled", "Attiva", "bool"], ["surcharge", "Sovrapprezzo €", "num"], ["title", "Titolo"], ["copy", "Testo"]] },
  { title: "Ordina online", section: "ordering", fields: [["glovo_url", "Link Glovo"], ["justeat_url", "Link Just Eat"], ["ordina_label", "Etichetta Ordina"], ["ordina_url", "Link Ordina qui"], ["ritira_label", "Etichetta Ritira"], ["ritira_url", "Link Ritira qui"]] },
  { title: "Popup Club", section: "club_popup", fields: [["enabled", "Popup attivo", "bool"], ["delay_seconds", "Secondi prima di apparire", "num"], ["suppress_days", "Giorni di stop dopo chiusura", "num"]] },
  { title: "Testi UNGRIP Club (popup)", section: "club", fields: [["label", "Label"], ["headline", "Titolo"], ["body", "Testo"], ["input_placeholder", "Placeholder email"], ["cta", "CTA"], ["microcopy", "Microcopy a mano"], ["note", "Nota form"], ["success_headline", "Titolo successo"], ["success_copy", "Testo successo"], ["success_cta", "CTA successo"]] },
  { title: "GDPR & Email provider", section: "gdpr", fields: [["consent_version", "Versione consenso"], ["double_opt_in", "Double opt-in", "bool"]] },
];

function ReviewsEditor({ settings, setSettings }) {
  const rv = settings.reviews || { enabled: true, items: [] };
  const items = rv.items || [];
  const upd = (patch) => setSettings({ ...settings, reviews: { ...rv, ...patch } });
  const updItem = (i, k, v) => upd({ items: items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) });
  return (
    <fieldset className="bg-[#141414] border border-[#242424] rounded-xl p-4">
      <legend className="font-display font-black uppercase text-[#FFC20E] px-2">Recensioni Google sul sito</legend>
      <p className="text-white/50 text-xs mb-3">Copia voto e recensioni reali dalla tua scheda Google. Se vuoto, non viene mostrato nulla.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-white/70 text-sm"><input type="checkbox" checked={rv.enabled !== false} onChange={(e) => upd({ enabled: e.target.checked })} className="accent-[#FFC20E]" data-testid="set-reviews-enabled" /> Mostra recensioni</label>
        <input className="ung-input" type="number" step="0.1" placeholder="Voto (es. 4.5)" value={rv.rating ?? ""} onChange={(e) => upd({ rating: e.target.value === "" ? null : Number(e.target.value) })} data-testid="set-reviews-rating" />
        <input className="ung-input" type="number" placeholder="N. recensioni" value={rv.count ?? ""} onChange={(e) => upd({ count: e.target.value === "" ? null : Number(e.target.value) })} data-testid="set-reviews-count" />
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid sm:grid-cols-[1fr_80px_2fr_auto] gap-2 mt-3">
          <input className="ung-input" placeholder="Nome" value={it.author || ""} onChange={(e) => updItem(i, "author", e.target.value)} data-testid={`set-review-author-${i}`} />
          <input className="ung-input" type="number" min="1" max="5" placeholder="Stelle" value={it.stars ?? 5} onChange={(e) => updItem(i, "stars", Number(e.target.value))} data-testid={`set-review-stars-${i}`} />
          <input className="ung-input" placeholder="Testo recensione" value={it.text || ""} onChange={(e) => updItem(i, "text", e.target.value)} data-testid={`set-review-text-${i}`} />
          <button onClick={() => upd({ items: items.filter((_, j) => j !== i) })} className="text-[#E63946] p-2" data-testid={`set-review-delete-${i}`}><Trash2 size={18} /></button>
        </div>
      ))}
      {items.length < 6 && (
        <button onClick={() => upd({ items: [...items, { author: "", stars: 5, text: "" }] })} className="btn-ghost mt-3" data-testid="set-review-add"><Plus size={16} /> Aggiungi recensione</button>
      )}
    </fieldset>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [editing, setEditing] = useState(null); // id | 'new'
  const [msg, setMsg] = useState("");

  const loadProducts = async () => {
    const { data } = await api.get("/products/all");
    setProducts(data);
  };
  const loadSubs = async () => {
    const { data } = await api.get("/club/subscribers");
    setSubs(data);
  };
  const loadSettings = async () => {
    const { data } = await api.get("/settings");
    setSettings(data);
  };

  useEffect(() => { loadProducts(); loadSubs(); loadSettings(); }, []);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 2500); };

  const saveProduct = async (data) => {
    if (editing === "new") {
      await api.post("/products", data);
    } else {
      await api.put(`/products/${editing}`, data);
    }
    setEditing(null);
    await loadProducts();
    flash("Prodotto salvato");
  };

  const removeProduct = async (id) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/products/${id}`);
    await loadProducts();
    flash("Prodotto eliminato");
  };

  const removeSub = async (id) => {
    if (!window.confirm("Eliminare (GDPR) questo iscritto?")) return;
    await api.delete(`/club/subscribers/${id}`);
    await loadSubs();
    flash("Iscritto eliminato");
  };

  const saveSettings = async () => {
    await api.put("/settings", settings);
    flash("Impostazioni salvate");
  };

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white">
      <style>{`
        .ung-input{background:#141414;border:1px solid #333;border-radius:8px;padding:10px 12px;color:#fff;outline:none;width:100%}
        .ung-input:focus{border-color:#FFC20E}
        .btn-yellow{display:inline-flex;align-items:center;gap:6px;background:#FFC20E;color:#0C0C0C;font-weight:800;text-transform:uppercase;border-radius:9999px;padding:8px 18px;font-family:'Barlow Condensed'}
        .btn-ghost{display:inline-flex;align-items:center;gap:6px;border:1px solid #444;color:#fff;border-radius:9999px;padding:8px 18px;text-transform:uppercase;font-family:'Barlow Condensed';font-weight:700}
      `}</style>

      <header className="header-gradient px-4 sm:px-6 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <img src="/assets/10_logo_ungrip_transparent.png" alt="UNGRIP" className="h-9" />
          <span className="font-display font-black uppercase text-white/90 hidden sm:block">CMS Dashboard</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-black/80 text-sm font-semibold hidden sm:block">{user?.email}</span>
          <button onClick={doLogout} className="inline-flex items-center gap-1 bg-[#0C0C0C] text-[#FFC20E] rounded-full px-4 py-2 font-display font-bold uppercase text-sm" data-testid="admin-logout-button">
            <LogOut size={16} /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        {msg && <div className="mb-4 rounded-lg bg-[#FFC20E] text-[#0C0C0C] px-4 py-2 font-bold" data-testid="admin-flash">{msg}</div>}

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: "products", label: "Prodotti", Icon: Package },
            { id: "subscribers", label: "Club", Icon: Users },
            { id: "settings", label: "Impostazioni", Icon: Cog },
          ].map(({ id, label, Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-display font-bold uppercase ${tab === id ? "bg-[#FFC20E] text-[#0C0C0C]" : "border border-[#333] text-white"}`}
              data-testid={`admin-tab-${id}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div>
            <button onClick={() => setEditing("new")} className="btn-yellow mb-4" data-testid="admin-add-product">
              <Plus size={16} /> Nuovo prodotto
            </button>
            {editing === "new" && <ProductForm onSave={saveProduct} onCancel={() => setEditing(null)} />}
            <div className="space-y-3 mt-3">
              {products.map((p) => (
                <div key={p.id} className="bg-[#141414] border border-[#242424] rounded-xl p-3" data-testid={`admin-product-${p.slug}`}>
                  <div className="flex items-center gap-3">
                    <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover bg-[#0C0C0C]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-black uppercase text-lg text-[#FFC20E] leading-none">{p.name}</p>
                      <p className="text-white/50 text-xs mt-1">{p.category} · #{p.sort_order} · {p.available ? "disponibile" : "nascosto"}{p.price != null ? ` · € ${Number(p.price).toFixed(2)}` : ""}</p>
                    </div>
                    <button onClick={() => setEditing(p.id)} className="btn-ghost !py-1.5" data-testid={`admin-edit-${p.slug}`}>Modifica</button>
                    <button onClick={() => removeProduct(p.id)} className="text-[#E63946] p-2" data-testid={`admin-delete-${p.slug}`}><Trash2 size={18} /></button>
                  </div>
                  {editing === p.id && <ProductForm initial={p} onSave={saveProduct} onCancel={() => setEditing(null)} />}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div>
            <p className="text-white/60 mb-3">{subs.length} iscritti al Club</p>
            <div className="overflow-x-auto rounded-xl border border-[#242424]">
              <table className="w-full text-sm" data-testid="admin-subscribers-table">
                <thead className="bg-[#141414] text-white/60 text-left">
                  <tr><th className="p-3">Email</th><th className="p-3">Data</th><th className="p-3">Consenso</th><th className="p-3">Source</th><th className="p-3"></th></tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-[#1c1c1c]">
                      <td className="p-3">{s.email}</td>
                      <td className="p-3 text-white/50">{(s.created_at || "").slice(0, 10)}</td>
                      <td className="p-3">{s.marketing_consent ? "✓" : "—"} v{s.consent_version}</td>
                      <td className="p-3 text-white/50">{s.utm_source || s.source}</td>
                      <td className="p-3"><button onClick={() => removeSub(s.id)} className="text-[#E63946]" data-testid={`admin-sub-delete-${s.id}`}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40">Nessun iscritto ancora.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="space-y-6 max-w-2xl">
            <fieldset className="bg-[#141414] border border-[#242424] rounded-xl p-4">
              <legend className="font-display font-black uppercase text-[#FFC20E] px-2">Location</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="ung-input" value={settings.location?.name || ""} onChange={(e) => setSettings({ ...settings, location: { ...settings.location, name: e.target.value } })} placeholder="Nome" data-testid="set-loc-name" />
                <input className="ung-input" value={settings.location?.address || ""} onChange={(e) => setSettings({ ...settings, location: { ...settings.location, address: e.target.value } })} placeholder="Indirizzo" data-testid="set-loc-address" />
                <input className="ung-input" value={settings.location?.city || ""} onChange={(e) => setSettings({ ...settings, location: { ...settings.location, city: e.target.value } })} placeholder="Città" />
                <input className="ung-input" value={settings.location?.maps_query || ""} onChange={(e) => setSettings({ ...settings, location: { ...settings.location, maps_query: e.target.value } })} placeholder="Query mappa" />
              </div>
            </fieldset>

            <fieldset className="bg-[#141414] border border-[#242424] rounded-xl p-4">
              <legend className="font-display font-black uppercase text-[#FFC20E] px-2">Social & Contatti</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="ung-input" value={settings.social?.facebook || ""} onChange={(e) => setSettings({ ...settings, social: { ...settings.social, facebook: e.target.value } })} placeholder="Facebook URL" />
                <input className="ung-input" value={settings.contact?.email || ""} onChange={(e) => setSettings({ ...settings, contact: { ...settings.contact, email: e.target.value } })} placeholder="Email contatto" />
              </div>
            </fieldset>

            <fieldset className="bg-[#141414] border border-[#242424] rounded-xl p-4">
              <legend className="font-display font-black uppercase text-[#FFC20E] px-2">Analytics (ID)</legend>
              <div className="grid sm:grid-cols-3 gap-3">
                <input className="ung-input" value={settings.analytics?.ga4_id || ""} onChange={(e) => setSettings({ ...settings, analytics: { ...settings.analytics, ga4_id: e.target.value } })} placeholder="GA4 ID" data-testid="set-ga4" />
                <input className="ung-input" value={settings.analytics?.gtm_id || ""} onChange={(e) => setSettings({ ...settings, analytics: { ...settings.analytics, gtm_id: e.target.value } })} placeholder="GTM ID" />
                <input className="ung-input" value={settings.analytics?.meta_pixel_id || ""} onChange={(e) => setSettings({ ...settings, analytics: { ...settings.analytics, meta_pixel_id: e.target.value } })} placeholder="Meta Pixel ID" />
              </div>
            </fieldset>

            {EXTRA_GROUPS.map((g) => <SettingsGroup key={g.section} {...g} settings={settings} setSettings={setSettings} />)}

            <ReviewsEditor settings={settings} setSettings={setSettings} />

            <div className="flex flex-wrap gap-3">
            <Link to="/admin/qr-recensioni" className="btn-ghost" data-testid="admin-review-qr-link"><QrCode size={16} /> QR recensioni da stampare</Link>
            <Link to="/admin/qr-sacchetti" className="btn-ghost" data-testid="admin-bag-qr-link"><QrCode size={16} /> QR adesivi sacchetti</Link>
            </div>

            <button onClick={saveSettings} className="btn-yellow" data-testid="admin-save-settings"><Save size={16} /> Salva impostazioni</button>
          </div>
        )}
      </div>
    </div>
  );
}
